import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Chip,
  Link,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Reply as ReplyIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { 
  db,
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  arrayUnion 
} from '../../firebase/firebase';

const ReviewManagement = () => {
  const { currentUser, isAdmin } = useAuth();
  
  // 评价状态
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // 评价详情
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [targetDetails, setTargetDetails] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);
  
  // 回复表单
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replying, setReplying] = useState(false);
  
  // 确认对话框
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
  });
  
  // 查看图片
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  
  // 通知
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // 加载评价 - 直接从Firestore获取
  const loadReviews = async () => {
    if (!currentUser || !isAdmin) {
      setError('无权访问管理页面');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 构建基础查询
      let reviewsRef = collection(db, 'reviews');
      let reviewsQuery = query(reviewsRef);
      
      // 添加筛选条件
      if (statusFilter !== 'all') {
        reviewsQuery = query(reviewsQuery, where('status', '==', statusFilter));
      }
      
      if (typeFilter !== 'all') {
        reviewsQuery = query(reviewsQuery, where('targetType', '==', typeFilter));
      }
      
      // 根据排序顺序添加排序
      const sortField = 'createdAt';
      const sortOrder = sortDirection === 'desc' ? 'desc' : 'asc';
      reviewsQuery = query(reviewsQuery, orderBy(sortField, sortOrder));
      
      // 先获取总数以计算分页
      const countSnapshot = await getDocs(reviewsQuery);
      const totalCount = countSnapshot.size;
      setTotalCount(totalCount);
      
      // 添加分页限制
      reviewsQuery = query(reviewsQuery, limit(rowsPerPage));
      
      // 如果不是第一页，需要计算起始位置
      if (page > 0) {
        // 注意：这是简化的方法，不是最高效的。对于大型集合，应该使用cursor pagination
        const startAfterDoc = countSnapshot.docs[page * rowsPerPage - 1];
        if (startAfterDoc) {
          reviewsQuery = query(reviewsQuery, startAfter(startAfterDoc));
        }
      }
      
      // 执行查询
      const querySnapshot = await getDocs(reviewsQuery);
      
      // 处理查询结果
      const reviewsList = [];
      for (const doc of querySnapshot.docs) {
        const reviewData = doc.data();
        reviewsList.push({
          id: doc.id,
          ...reviewData,
          createdAt: reviewData.createdAt?.toDate() || new Date(),
          updatedAt: reviewData.updatedAt?.toDate() || null
        });
      }
      
      // 获取评价的相关用户信息
      const reviewsWithUserInfo = await Promise.all(
        reviewsList.map(async (review) => {
          try {
            if (review.userId) {
              const userDoc = await getDoc(doc(db, 'users', review.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  ...review,
                  userName: userData.displayName || review.userName || '匿名用户',
                  userAvatar: userData.photoURL || review.userAvatar || null
                };
              }
            }
            return review;
          } catch (error) {
            console.error('获取用户信息失败:', error);
            return review;
          }
        })
      );
      
      setReviews(reviewsWithUserInfo);
      
    } catch (error) {
      console.error('加载评价出错:', error);
      setError('获取评价数据失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 首次加载和参数变化时重新加载评价
  useEffect(() => {
    loadReviews();
  }, [isAdmin, page, rowsPerPage, statusFilter, typeFilter, sortDirection]);

  // 搜索评价
  const handleSearch = () => {
    // 重置页码
    setPage(0);
    loadReviews();
  };

  // 获取评价目标详情（产品或命理师）
  const fetchTargetDetails = async (targetId, targetType) => {
    setTargetLoading(true);
    
    try {
      const db = getFirestore();
      const collection = targetType === 'product' ? 'products' : 'fortuneTellers';
      const docRef = doc(db, collection, targetId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setTargetDetails({
          id: docSnap.id,
          ...docSnap.data(),
          type: targetType
        });
      } else {
        setTargetDetails(null);
      }
    } catch (error) {
      console.error('获取目标详情失败:', error);
      setTargetDetails(null);
    } finally {
      setTargetLoading(false);
    }
  };

  // 查看评价详情
  const handleViewDetails = (review) => {
    setSelectedReview(review);
    setDetailsOpen(true);
    
    // 加载目标详情
    if (review.targetId && review.targetType) {
      fetchTargetDetails(review.targetId, review.targetType);
    }
  };

  // 回复评价
  const handleReply = (review) => {
    setSelectedReview(review);
    setReplyContent('');
    setReplyOpen(true);
  };

  // 提交回复 - 直接使用Firestore
  const submitReply = async () => {
    if (!selectedReview || !replyContent.trim()) return;
    
    setReplying(true);
    
    try {
      if (!currentUser || !isAdmin) {
        throw new Error('只有管理员可以回复评价');
      }
      
      const userId = currentUser.uid;
      
      // 获取管理员用户信息
      const adminUserDoc = await getDoc(doc(db, 'users', userId));
      const adminUserData = adminUserDoc.exists() ? adminUserDoc.data() : {};
      
      // 创建回复对象
      const reply = {
        userId,
        userName: adminUserData.displayName || '管理员',
        content: replyContent.trim(),
        createdAt: new Date(),
        isAdmin: true
      };
      
      // 更新评价文档，添加回复
      const reviewRef = doc(db, 'reviews', selectedReview.id);
      await updateDoc(reviewRef, {
        replies: arrayUnion(reply),
        hasReply: true,
        updatedAt: serverTimestamp()
      });
      
      setSnackbar({
        open: true,
        message: '回复成功',
        severity: 'success'
      });
      
      // 更新本地评价数据
      setReviews(reviews.map(review => 
        review.id === selectedReview.id ? {
          ...review,
          hasReply: true,
          replies: [...(review.replies || []), reply]
        } : review
      ));
      
      // 如果详情页面打开，更新详情数据
      if (detailsOpen) {
        setSelectedReview({
          ...selectedReview,
          hasReply: true,
          replies: [...(selectedReview.replies || []), reply]
        });
      }
      
      // 关闭回复对话框
      setReplyOpen(false);
    } catch (error) {
      console.error('回复评价出错:', error);
      setSnackbar({
        open: true,
        message: '回复失败: ' + error.message,
        severity: 'error'
      });
    } finally {
      setReplying(false);
    }
  };

  // 更改评价状态 - 直接使用Firestore
  const updateReviewStatus = async (reviewId, newStatus) => {
    setLoading(true);
    
    try {
      if (!currentUser || !isAdmin) {
        throw new Error('只有管理员可以更改评价状态');
      }
      
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewDoc = await getDoc(reviewRef);
      
      if (!reviewDoc.exists()) {
        throw new Error('评价不存在');
      }
      
      const reviewData = reviewDoc.data();
      
      // 更新评价状态
      await updateDoc(reviewRef, {
        status: newStatus,
        adminNotes: `管理员于${new Date().toLocaleString()}将状态更改为${newStatus}`,
        updatedAt: serverTimestamp()
      });
      
      // 如果状态变更会影响评分计算，需要重新计算目标对象的评分
      if (newStatus !== reviewData.status && reviewData.targetId && reviewData.targetType) {
        // 重新计算目标评分
        await recalculateTargetRating(reviewData.targetId, reviewData.targetType);
      }
      
      setSnackbar({
        open: true,
        message: '状态更新成功',
        severity: 'success'
      });
      
      // 更新本地评价数据
      setReviews(reviews.map(review => 
        review.id === reviewId ? {
          ...review,
          status: newStatus
        } : review
      ));
      
      // 如果详情页面打开，更新详情数据
      if (detailsOpen && selectedReview.id === reviewId) {
        setSelectedReview({
          ...selectedReview,
          status: newStatus
        });
      }
    } catch (error) {
      console.error('更新评价状态出错:', error);
      setSnackbar({
        open: true,
        message: '更新状态失败: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // 重新计算目标评分
  const recalculateTargetRating = async (targetId, targetType) => {
    try {
      // 获取所有状态为active的评价
      const activeReviewsQuery = query(
        collection(db, 'reviews'),
        where('targetId', '==', targetId),
        where('targetType', '==', targetType),
        where('status', '==', 'published')
      );
      
      const reviewsSnapshot = await getDocs(activeReviewsQuery);
      
      // 计算新的评分
      let totalRating = 0;
      let count = 0;
      
      reviewsSnapshot.forEach(doc => {
        const reviewData = doc.data();
        totalRating += reviewData.rating || 0;
        count++;
      });
      
      const averageRating = count > 0 ? totalRating / count : 0;
      
      // 更新目标对象的评分
      const collectionName = targetType === 'product' ? 'products' : 'fortuneTellers';
      const targetRef = doc(db, collectionName, targetId);
      
      await updateDoc(targetRef, {
        averageRating,
        reviewCount: count,
        updatedAt: serverTimestamp()
      });
      
      // 更新评分统计缓存
      const statsCollectionName = targetType === 'product' ? 'productStats' : 'fortuneTellerStats';
      const statsRef = doc(db, statsCollectionName, targetId);
      
      // 获取当前统计
      const statsDoc = await getDoc(statsRef);
      
      if (statsDoc.exists()) {
        // 重新计算评分分布
        const ratingDistribution = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0};
        
        reviewsSnapshot.forEach(doc => {
          const reviewData = doc.data();
          const rating = Math.min(Math.max(Math.round(reviewData.rating || 0), 1), 5);
          ratingDistribution[rating]++;
        });
        
        await updateDoc(statsRef, {
          totalReviews: count,
          averageRating,
          ratingDistribution,
          lastUpdated: serverTimestamp()
        });
      }
      
      return true;
    } catch (error) {
      console.error('重新计算评分失败:', error);
      return false;
    }
  };

  // 确认删除评价
  const confirmDeleteReview = (review) => {
    setConfirmDialog({
      open: true,
      title: '确认删除评价',
      message: '此操作将删除该评价并更新相关产品或命理师的评分，确定要继续吗？',
      action: () => updateReviewStatus(review.id, 'deleted')
    });
  };

  // 确认隐藏评价
  const confirmHideReview = (review) => {
    setConfirmDialog({
      open: true,
      title: '确认隐藏评价',
      message: '此操作将隐藏该评价并更新相关产品或命理师的评分，确定要继续吗？',
      action: () => updateReviewStatus(review.id, 'hidden')
    });
  };

  // 确认激活评价 (发布状态应为 published 与其他组件保持一致)
  const confirmActivateReview = (review) => {
    setConfirmDialog({
      open: true,
      title: '确认激活评价',
      message: '此操作将激活该评价并更新相关产品或命理师的评分，确定要继续吗？',
      action: () => updateReviewStatus(review.id, 'published')
    });
  };

  // 页码变化
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 每页行数变化
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 查看图片
  const handleViewImage = (url) => {
    setCurrentImage(url);
    setImageViewerOpen(true);
  };

  // 格式化日期
  const formatDate = (date) => {
    if (!date) return '未知日期';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : 
                    date.seconds ? new Date(date.seconds * 1000) : date;
      return format(dateObj, 'yyyy-MM-dd HH:mm:ss', { locale: zhCN });
    } catch (error) {
      return '日期格式错误';
    }
  };

  // 获取目标类型标签
  const getTargetTypeLabel = (type) => {
    switch (type) {
      case 'product':
        return '商品';
      case 'fortuneTeller':
        return '命理师';
      default:
        return '未知';
    }
  };

  // 获取状态标签
  const getStatusChip = (status) => {
    switch (status) {
      case 'published':
        return <Chip size="small" label="已显示" color="success" />;
      case 'hidden':
        return <Chip size="small" label="已隐藏" color="warning" />;
      case 'deleted':
        return <Chip size="small" label="已删除" color="error" />;
      default:
        return <Chip size="small" label="未知状态" />;
    }
  };

  // 无权限访问
  if (!isAdmin) {
    return (
      <Alert severity="error" sx={{ my: 4 }}>
        您没有访问该页面的权限
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        评价管理
      </Typography>
      
      {/* 搜索和筛选 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="搜索评价内容"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleSearch}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>评价状态</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="评价状态"
              >
                <MenuItem value="all">全部状态</MenuItem>
                <MenuItem value="published">已显示</MenuItem>
                <MenuItem value="hidden">已隐藏</MenuItem>
                <MenuItem value="deleted">已删除</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>评价类型</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="评价类型"
              >
                <MenuItem value="all">全部类型</MenuItem>
                <MenuItem value="product">商品评价</MenuItem>
                <MenuItem value="fortuneTeller">命理师评价</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Button 
              fullWidth
              variant="contained" 
              color="primary"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setTypeFilter('all');
                setSortDirection('desc');
                setPage(0);
                loadReviews();
              }}
              startIcon={<RefreshIcon />}
            >
              重置筛选
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* 错误提示 */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {/* 评价列表 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>用户</TableCell>
              <TableCell>评价目标</TableCell>
              <TableCell>评分</TableCell>
              <TableCell>内容</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>提交时间</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={30} sx={{ my: 3 }} />
                </TableCell>
              </TableRow>
            ) : reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    暂无评价数据
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map(review => (
                <TableRow key={review.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {review.userName || '匿名用户'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getTargetTypeLabel(review.targetType)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Rating value={review.rating} precision={0.5} readOnly size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {review.content}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(review.status)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(review.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Tooltip title="查看详情">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(review)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="回复">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleReply(review)}
                        >
                          <ReplyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {review.status === 'published' ? (
                        <Tooltip title="隐藏评价">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => confirmHideReview(review)}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="显示评价">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => confirmActivateReview(review)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="删除评价">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => confirmDeleteReview(review)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="每页行数"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}-${to} 共 ${count}`
          }
        />
      </TableContainer>
      
      {/* 评价详情对话框 */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          评价详情
        </DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      评价信息
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        评价ID:
                      </Typography>
                      <Typography variant="body1">
                        {selectedReview.id}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        用户:
                      </Typography>
                      <Typography variant="body1">
                        {selectedReview.userName} (ID: {selectedReview.userId})
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        评分:
                      </Typography>
                      <Rating value={selectedReview.rating} precision={0.5} readOnly />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        状态:
                      </Typography>
                      {getStatusChip(selectedReview.status)}
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        提交时间:
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedReview.createdAt)}
                      </Typography>
                    </Box>
                    {selectedReview.orderId && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          关联订单:
                        </Typography>
                        <Typography variant="body1">
                          {selectedReview.orderId}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
                
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      评价目标
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {targetLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : targetDetails ? (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            类型:
                          </Typography>
                          <Typography variant="body1">
                            {getTargetTypeLabel(targetDetails.type)}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            名称:
                          </Typography>
                          <Typography variant="body1">
                            {targetDetails.name}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            ID:
                          </Typography>
                          <Typography variant="body1">
                            {targetDetails.id}
                          </Typography>
                        </Box>
                        <Box>
                          <Link
                            href={targetDetails.type === 'product' ? 
                              `/product/${targetDetails.id}` : 
                              `/fortune-teller/${targetDetails.id}`}
                            target="_blank"
                            rel="noopener"
                          >
                            <Button variant="outlined" size="small">
                              查看详情页
                            </Button>
                          </Link>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body1" color="error">
                        无法加载目标详情
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      评价内容
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body1" paragraph>
                      {selectedReview.content}
                    </Typography>
                    
                    {/* 评价图片 */}
                    {selectedReview.photos && selectedReview.photos.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          评价图片:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedReview.photos.map((photo, index) => (
                            <Box
                              key={index}
                              component="img"
                              src={photo}
                              alt={`评价图片 ${index + 1}`}
                              onClick={() => handleViewImage(photo)}
                              sx={{
                                width: 100,
                                height: 100,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'pointer',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
                
                {/* 回复列表 */}
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        回复记录
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => handleReply(selectedReview)}
                      >
                        添加回复
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {selectedReview.replies && selectedReview.replies.length > 0 ? (
                      selectedReview.replies.map((reply, index) => (
                        <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < selectedReview.replies.length - 1 ? '1px solid #eee' : 'none' }}>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            回复时间: {formatDate(reply.createdAt)}
                          </Typography>
                          <Typography variant="body1">
                            {reply.content}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body1" color="textSecondary" sx={{ py: 2 }}>
                        暂无回复记录
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedReview && selectedReview.status === 'published' ? (
            <Button
              color="warning"
              onClick={() => {
                setDetailsOpen(false);
                confirmHideReview(selectedReview);
              }}
            >
              隐藏评价
            </Button>
          ) : selectedReview && (selectedReview.status === 'hidden' || selectedReview.status === 'deleted') ? (
            <Button
              color="success"
              onClick={() => {
                setDetailsOpen(false);
                confirmActivateReview(selectedReview);
              }}
            >
              显示评价
            </Button>
          ) : null}
          <Button
            color="primary"
            onClick={() => setDetailsOpen(false)}
          >
            关闭
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 回复对话框 */}
      <Dialog
        open={replyOpen}
        onClose={() => !replying && setReplyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          回复评价
        </DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    用户: {selectedReview.userName}
                  </Typography>
                  <Rating value={selectedReview.rating} precision={0.5} readOnly size="small" sx={{ mb: 1 }} />
                  <Typography variant="body1">
                    {selectedReview.content}
                  </Typography>
                </CardContent>
              </Card>
              
              <TextField
                label="回复内容"
                multiline
                rows={5}
                fullWidth
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                disabled={replying}
                placeholder="请输入回复内容..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReplyOpen(false)}
            disabled={replying}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitReply}
            disabled={replying || !replyContent.trim()}
          >
            {replying ? <CircularProgress size={24} /> : '提交回复'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 确认对话框 */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="primary"
          >
            取消
          </Button>
          <Button
            onClick={() => {
              confirmDialog.action();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            color="primary"
            variant="contained"
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 图片查看器 */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={currentImage}
            alt="评价图片"
            style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageViewerOpen(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewManagement;